import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { BadgeCheck } from 'lucide-react'
import type { CatalogDto, CatalogHostDto } from '../../data/types'
import { Button } from '../../ui/Button'
import { Card } from '../../ui/Card'
import { Sheet, SheetContent } from '../../ui/Sheet'
import type { DataTableColumn, DataTableSort } from '../../ui/DataTable'
import { DataGuard, type QueryLike } from '../shared/DataGuard'
import { CatalogAvailability } from './CatalogAvailability'
import { CatalogCapabilities } from './CatalogCapabilities'
import { CatalogFilterPanel, NARROW_VIEWPORT, useMediaQuery } from './CatalogFilterPanel'
import { CatalogToolbar } from './CatalogToolbar'
import { applyCatalogQuery, clearFilters, parseCatalogQuery, serializeCatalogQuery, withSortKey, type CatalogQuery, type SortKey } from './catalogQuery'
import { CatalogUnitPrices } from './CatalogUnitPrices'
import { freshnessNote, hostLabel, rentalPathHref, staleHostHint, trackRecordLabel, updatedAgo } from './hostLabels'
import { PageHeader } from '../shared/PageHeader'

const CatalogTable = lazy(() => import('./CatalogTable').then((module) => ({ default: module.CatalogTable })))

/*
 * The table header shows the catalog's sort only where a column holds what is
 * sorted. Region, tier and free memory have no column of their own, so for
 * them no header claims the sort — the Host header used to, whatever the list
 * was actually ordered by.
 */
const COLUMN_FOR_SORT: Record<SortKey, string | null> = { organization: 'host', freeCpu: 'available', price: 'rate', region: null, tier: null, freeMemory: null }
const SORT_FOR_COLUMN: Record<string, SortKey | undefined> = { host: 'organization', available: 'freeCpu', rate: 'price' }

function VerifiedMark({ host, auditDataAvailable }: { host: CatalogHostDto; auditDataAvailable: boolean }) {
  if (!auditDataAvailable) return null
  if (!host.audited) return <span className="sr-only">Unverified provider</span>
  return <span className="catalog-verified-mark" role="img" aria-label="Verified provider" title="Verified provider"><BadgeCheck size={16} strokeWidth={2.4} aria-hidden="true" /></span>
}

export function CatalogPage({ query, onNavigate, renderRentAction }: { query: QueryLike<CatalogDto>; onNavigate?: ((path: string) => void) | undefined; renderRentAction?: ((host: CatalogHostDto) => ReactNode) | undefined }) {
  const [catalogQuery, setCatalogQuery] = useState(() => parseCatalogQuery(new URLSearchParams(window.location.search)))
  // Closed on every visit, shared links included: the chips show what a link
  // applied, so the panel no longer has to be open to prove it.
  const [filtersOpen, setFiltersOpen] = useState(false)
  const narrow = useMediaQuery(NARROW_VIEWPORT)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const countRef = useRef<HTMLSpanElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  // Where focus goes once the panel has opened or closed, applied after the
  // render that does it so the target exists (or the sheet has let go).
  const pendingFocus = useRef<'sort' | 'count' | 'toggle' | null>(null)

  useEffect(() => {
    const suffix = serializeCatalogQuery(catalogQuery)
    window.history.replaceState({}, '', `/app/rent/hosts${suffix ? `?${suffix}` : ''}`)
  }, [catalogQuery])

  useEffect(() => {
    const target = pendingFocus.current
    if (!target) return undefined
    pendingFocus.current = null
    // Deferred a tick: closing the sheet hands focus back to its trigger as it
    // unmounts, and "Show N hosts" means to land on the count instead.
    const timer = window.setTimeout(() => {
      if (target === 'sort') panelRef.current?.querySelector<HTMLInputElement>('input[name="catalog-sort"]:checked')?.focus()
      else if (target === 'count') countRef.current?.focus()
      else toggleRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [filtersOpen])

  const closePanel = (focus: 'count' | 'toggle') => { pendingFocus.current = focus; setFiltersOpen(false) }
  const editSort = () => {
    // The sheet's focus trap focuses its first tabbable control, which is the
    // checked sort key because the segmented groups use a roving tabindex.
    if (narrow) { setFiltersOpen(true); return }
    if (filtersOpen) { panelRef.current?.querySelector<HTMLInputElement>('input[name="catalog-sort"]:checked')?.focus(); return }
    pendingFocus.current = 'sort'
    setFiltersOpen(true)
  }

  const regions = [...new Set((query.data?.hosts ?? []).map((host) => host.region).filter(Boolean))]
  const tiers = [...new Set((query.data?.hosts ?? []).map((host) => host.tier).filter(Boolean))]
  const hosts = useMemo(() => applyCatalogQuery(query.data?.hosts ?? [], catalogQuery), [catalogQuery, query.data?.hosts])

  const columns: DataTableColumn<CatalogHostDto>[] = [
    { id: 'host', header: 'Host', sortable: true, value: (host) => hostLabel(host), cell: (host) => <div className="catalog-host-cell"><div className="catalog-host-name"><strong>{hostLabel(host)}</strong><VerifiedMark host={host} auditDataAvailable={query.data?.auditDataAvailable ?? false} /></div><span className="muted">{host.region} · Tier {host.tier}</span></div> },
    { id: 'capabilities', header: 'Capabilities', cell: (host) => <CatalogCapabilities capacity={host.inventory} /> },
    { id: 'available', header: 'Available now', sortable: true, cell: (host) => {
      const note = freshnessNote(host)
      return <div className="catalog-available-cell"><CatalogAvailability host={host} />{note ? <span className="muted">{note}</span> : null}</div>
    } },
    { id: 'track', header: 'Track record', cell: (host) => <span className={host.track_record.state === 'rated' ? '' : 'muted'}>{trackRecordLabel(host)}</span> },
    // The provider's own rate card, per resource. It replaced a single
    // `reference_price` figure — the bidengine's indicative bid for the fixed
    // `medium-v1` shape, identical on every row — which priced a different
    // thing from what "Rent this host" opens.
    { id: 'rate', header: 'Price $/hour', sortable: true, cell: (host) => host.unit_prices
      ? <CatalogUnitPrices prices={host.unit_prices} />
      : <span className="muted">Unavailable</span> },
    { id: 'action', header: 'Action', cell: (host) => renderRentAction?.(host) ?? <Button size="sm" variant="primary" title={staleHostHint(host)} onClick={() => onNavigate?.(rentalPathHref(host.owner))}>Rent this host</Button> },
  ]

  // Bumped by both reset paths: it remounts the number fields, so text that
  // never parsed — and so never reached the query — is cleared with the rest.
  const [resetEpoch, setResetEpoch] = useState(0)
  const resetQuery = (next: (current: CatalogQuery) => CatalogQuery) => { setResetEpoch((epoch) => epoch + 1); setCatalogQuery(next) }
  const clearAllFilters = () => resetQuery(clearFilters)
  const tableColumn = COLUMN_FOR_SORT[catalogQuery.sort]
  const tableSort: DataTableSort | null = tableColumn ? { id: tableColumn, direction: catalogQuery.direction === 'asc' ? 'ascending' : 'descending' } : null
  const onTableSortChange = (next: DataTableSort) => {
    const key = SORT_FOR_COLUMN[next.id]
    if (!key) return
    // The same column flips; a new one starts at its key's own default.
    setCatalogQuery((current) => current.sort === key ? { ...current, direction: current.direction === 'asc' ? 'desc' : 'asc' } : withSortKey(current, key))
  }
  const panel = <CatalogFilterPanel
    id={narrow ? 'catalog-filters' : undefined}
    query={catalogQuery}
    onChange={setCatalogQuery}
    onReset={(next) => resetQuery(() => next)}
    resetEpoch={resetEpoch}
    regions={regions}
    tiers={tiers}
    resultCount={hosts.length}
    auditDataAvailable={query.data?.auditDataAvailable ?? false}
    onDone={() => closePanel('count')}
  />
  return <>
    {/* Door B remains available from the marketplace header; a host-row action
        below is Door A and carries the selected host into configure. */}
    <PageHeader back={{ label: 'Back', onClick: () => onNavigate?.('/app/rent') }} eyebrow="Marketplace" title="Rent a machine" description="Browse hosts with capacity to run your next workload." actions={<Button variant="primary" onClick={() => onNavigate?.(rentalPathHref())}>Rent without choosing a host</Button>} />
    <DataGuard query={query} empty={{ title: 'No hosts are registered on this network yet', body: 'When hosts publish capacity, they will appear here.', action: <Button onClick={() => void query.refetch()}>Refresh</Button> }} loading={<CatalogSkeleton />}>
      {(data) => <>
        {!data.auditDataAvailable && <div className="notice notice-caution">Verification status is unknown right now. The host list itself is complete.</div>}
        <CatalogToolbar
          query={catalogQuery}
          onChange={setCatalogQuery}
          onClearFilters={clearAllFilters}
          resultCount={hosts.length}
          open={filtersOpen}
          onToggle={() => { if (filtersOpen) closePanel('toggle'); else setFiltersOpen(true) }}
          onEditSort={editSort}
          toggleRef={toggleRef}
          countRef={countRef}
        />
        {narrow
          ? <Sheet open={filtersOpen} onOpenChange={(open) => { if (!open) setFiltersOpen(false) }}><SheetContent title="Filter and sort" className="catalog-filter-sheet">{panel}</SheetContent></Sheet>
          : <div ref={panelRef} id="catalog-filters" className="catalog-filter-panel" hidden={!filtersOpen} onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); closePanel('toggle') } }}>{filtersOpen && panel}</div>}
        {hosts.length === 0 ? <div className="empty-state compact"><h2>{data.count === 0 ? 'No hosts are registered on this network yet' : 'No hosts match these filters'}</h2><p>{data.count === 0 ? 'When hosts publish capacity, they will appear here.' : 'Clear a filter to see the full marketplace.'}</p>{data.count === 0 ? <Button onClick={query.refetch}>Refresh</Button> : <Button onClick={clearAllFilters}>Clear filters</Button>}</div> : <><div className="catalog-desktop-table"><Suspense fallback={<CatalogSkeleton />}><CatalogTable rows={hosts} columns={columns} sort={tableSort} onSortChange={onTableSortChange} /></Suspense></div><div className="catalog-grid catalog-mobile-cards">{hosts.map((dto) => { const name = dto.organization || 'Assigned host'; return <Card key={dto.owner} className="host-card"><div className="host-card-content"><div><h2 className="catalog-host-name">{name}<VerifiedMark host={dto} auditDataAvailable={data.auditDataAvailable} /></h2><p className="muted">{dto.region} · {dto.tier}</p></div><CatalogCapabilities capacity={dto.inventory} /><div className="host-card-availability"><span className="muted">Available now</span><CatalogAvailability host={dto} /></div><div className="host-card-availability"><span className="muted">Price $/hour</span>{dto.unit_prices ? <CatalogUnitPrices prices={dto.unit_prices} /> : <span className="muted">Unavailable</span>}</div><div className="host-card-bottom"><span className="muted">Track record: {trackRecordLabel(dto)}</span><span className="muted">{freshnessNote(dto) ?? updatedAgo(dto.collected_at) ?? ''}</span></div></div>{(renderRentAction?.(dto) ?? <Button variant="primary" title={staleHostHint(dto)} onClick={() => onNavigate?.(rentalPathHref(dto.owner))}>Rent this host</Button>)}</Card>})}</div></>}
      </>}
    </DataGuard>
  </>
}

function CatalogSkeleton() { return <div className="catalog-grid">{Array.from({ length: 6 }, (_, index) => <Card key={index}><div className="skeleton-stack"><span className="skeleton skeleton-title" /><span className="skeleton" /><span className="skeleton" /></div></Card>)}</div> }
